// Sentry for the staff and customer apps (@sentry/react), shared setup.
//
// - Off unless the build has VITE_SENTRY_DSN: then nothing is loaded at all. With a
//   DSN the SDK is loaded with a dynamic import (the app passes `() =>
//   import('@sentry/react')`), so it never counts against the first-load budget.
// - Personal data: Sentry's dataCollection collects none, and every event and
//   breadcrumb passes through scrubEvent / scrubBreadcrumb (./scrub.ts): no user, no
//   bodies, no cookies, tokens/PINs/OTPs/phones/emails masked in URLs and messages.
// - Expected API problems (an ApiError below 500: wrong PIN, validation, 409…) are
//   not reported. A reported API error is tagged with its request id; the staff app
//   tags the company of the session (never the user).
import { scrubBreadcrumb, scrubEvent, sentryDataCollection } from './scrub.js';

/** The part of @sentry/react this module uses. */
export interface BrowserSentryModule {
  init(options: never): unknown;
  setTag(key: string, value: string | undefined): void;
}

export interface BrowserSentryConfig {
  /** import.meta.env.VITE_SENTRY_DSN */
  readonly dsn: string | undefined;
  /** import.meta.env.VITE_SENTRY_ENVIRONMENT, else `mode`. */
  readonly environment: string | undefined;
  /** import.meta.env.VITE_SENTRY_RELEASE (the deployed git sha). */
  readonly release: string | undefined;
  readonly app: 'staff' | 'customer';
  /** import.meta.env.MODE */
  readonly mode: string;
}

interface Hint {
  readonly originalException?: unknown;
}

let current: BrowserSentryModule | undefined;

/** An ApiError-like object (packages/api-client) without importing it. */
function apiFailure(error: unknown): { status: number; requestId: string | undefined } | null {
  if (error === null || typeof error !== 'object') return null;
  const { status, requestId } = error as { status?: unknown; requestId?: unknown };
  if (typeof status !== 'number') return null;
  return { status, requestId: typeof requestId === 'string' ? requestId : undefined };
}

/** The options handed to Sentry.init (exported for tests). */
export function browserSentryOptions(config: BrowserSentryConfig): Record<string, unknown> {
  return {
    dsn: config.dsn,
    environment: config.environment ?? config.mode,
    ...(config.release ? { release: config.release } : {}),
    dataCollection: sentryDataCollection(),
    // Errors only: no tracing, no session replay.
    maxBreadcrumbs: 30,
    initialScope: { tags: { app: config.app } },
    beforeSend: (event: Record<string, unknown>, hint?: Hint) => {
      const api = apiFailure(hint?.originalException);
      if (api && api.status > 0 && api.status < 500) return null;
      const scrubbed = scrubEvent(event);
      if (api?.requestId) {
        scrubbed.tags = { ...(scrubbed.tags as object | undefined), request_id: api.requestId };
      }
      return scrubbed;
    },
    beforeBreadcrumb: (breadcrumb: Record<string, unknown>) => scrubBreadcrumb(breadcrumb),
  };
}

/** Start Sentry when the build has a DSN; returns whether it started. */
export async function startBrowserSentry(
  config: BrowserSentryConfig,
  load: () => Promise<BrowserSentryModule>,
): Promise<boolean> {
  if (!config.dsn || config.dsn.trim() === '') return false;
  const sentry = await load();
  sentry.init(browserSentryOptions(config) as never);
  current = sentry;
  return true;
}

/** Tag later reports with the session's company (staff app); undefined clears it. */
export function setSentryCompany(companyId: string | undefined): void {
  current?.setTag('company_id', companyId);
}
